import { CSSProperties, useEffect, useState } from 'react'
import { Switch, Route, useLocation } from 'react-router-dom'
import useScreen from 'Hooks/useScreen';
import { styx } from 'modules/Utils';
import { PlaybookType } from 'modules/Const';
import HomePage from 'pages/HomePage';
import Colors from 'modules/Colors';
import MarketingCampaignDetails from 'pages/MarketingCampaignDetails/MarketingCampaignDetails';
import Pricing from 'pages/Pricing/Pricing';
import Navbar from 'components/Navbar/Navbar';
import Footer from 'components/Footer/Footer';
import AssistantPage from 'pages/AssistantPage';
import NotFoundPage from 'pages/NotFoundPage';
import useBrand from 'Hooks/useBrand';
import LoaderOverlay from 'elements/LoaderOverlay';
import useMaestroChat from 'Hooks/useMaestroChat';
import { OLD_SITE_URL } from 'modules/Utils';
import { useAuth0 } from '@auth0/auth0-react';
import { checkIsPostAuthRoute } from './PostAuthRoutes';

// Define routes that should redirect to external URLs
const externalRoutes: Record<string, string> = {
    '/about': `${OLD_SITE_URL}/about`,
    '/blog': `${OLD_SITE_URL}/blog`,
    '/case-studies': `${OLD_SITE_URL}/case-studies`,
    '/solutions/website-creation': `${OLD_SITE_URL}/solutions/website-creation`,
    '/solutions/automated-marketing': `${OLD_SITE_URL}/solutions/automated-marketing`,
    '/solutions/fan-insights': `${OLD_SITE_URL}/solutions/fan-insights`,
    '/what-is-possible/identify-and-understand-your-audience': `${OLD_SITE_URL}/what-is-possible/identify-and-understand-your-audience`,
    '/help-center': 'https://support.symphonyos.co/en/',
    '/video-walkthroughs': 'https://www.youtube.com/@Symphony-OS',
};

// Helper function to check if a route should redirect externally
const checkIsExternalRoute = (pathname: string): string | null => {
    // Check exact matches first
    if (externalRoutes[pathname]) {
        return externalRoutes[pathname];
    }
    
    // Check for partnership patterns (existing logic)
    const partnershipsPatterns = ['/partners/', '/partnerships'];
    const shouldRedirectToOldSite = partnershipsPatterns.some(pattern => pathname.includes(pattern));
    
    if (shouldRedirectToOldSite) {
        return `${OLD_SITE_URL}${pathname}`;
    }
    
    return null;
};

// Helper function to check if a route is defined in publicRoutes
const checkIsPublicRoute = (pathname: string): boolean => {
    return publicRoutes.some(route => {
        const paths = Array.isArray(route.path) ? route.path : [route.path];
        return paths.some(path => {
            if (path.includes(':')) {
                // Handle dynamic routes like /chats/:chatId
                const pathPattern = path.replace(/:[^/]+/g, '[^/]+');
                const regex = new RegExp(`^${pathPattern}$`);
                return regex.test(pathname);
            }
            return path === pathname;
        });
    });
};

// Define public routes configuration
const publicRoutes = [
    {
        path: ['/', '/chats/:chatId'],
        exact: true,
        component: <AssistantPage isPublic />,
    },
    {
        path: '/adbox/campaigns/:campaignId',
        exact: true,
        component: (
            <MarketingCampaignDetails
                className="marketingCampaignDetailsComponentPublic"
                style={{ position: 'relative' }}
                isPublic
            />
        ),
    },
    // {
    //     path: '/pricing',
    //     exact: true,
    //     component: <Pricing />,
    // },
    {
        path: '*',
        exact: false,
        component: <NotFoundPage isPublic />,
    },
];

const PublicRoutes = () => {
    const location = useLocation();
    const { isChatOpen } = useMaestroChat();
    const { loginWithRedirect } = useAuth0();

    // Check for external routes and redirect
    useEffect(() => {
        const { pathname, search } = location;
        const externalUrl = checkIsExternalRoute(pathname);
        
        if (externalUrl) {
            // Add search params if they exist
            const fullUrl = search ? `${externalUrl}${search}` : externalUrl;
            window.location.href = fullUrl;
        }
    }, [location]);

    // Check if current path matches a post-auth route
    const isPostAuthRoute = checkIsPostAuthRoute(location.pathname);
    
    // Check if current path should redirect externally
    const shouldRedirectExternally = checkIsExternalRoute(location.pathname) !== null;

    // Check if current path is also available as a public route
    const isPublicRoute = checkIsPublicRoute(location.pathname);

    // Redirect to Auth0 login for post-auth routes (but not for external routes or routes that are also public)
    useEffect(() => {
        if (isPostAuthRoute && !shouldRedirectExternally && !isPublicRoute) {
            // Preserve the user's intended destination
            const returnToUrl = `${window.location.origin}${location.pathname}${location.search}`;
            loginWithRedirect({
                appState: {
                    returnTo: location.pathname + location.search
                }
            });
        }
    }, [isPostAuthRoute, shouldRedirectExternally, isPublicRoute, location.pathname, location.search]);

    const getFlexDirection = () => {
        if (location.pathname.includes('/adbox/campaigns/')) {
            return 'row';
        }
        return 'column';
    }

    const renderRoutes = () => {
        return (
            <div
                className="publicRoutesMainContent"
                style={styx(
                    sty.publicRoutesComponent,
                    { flexDirection: getFlexDirection() }
                )}
            >
                <Navbar className="navbarFromPublicRoutes" />
                <div className="publicRoutesContentArea" style={sty.contentArea}>
                    <Switch>
                        {publicRoutes.map((route, index) => (
                            <Route
                                key={index}
                                exact={route.exact}
                                path={route.path}
                                children={route.component}
                            />
                        ))}
                    </Switch>
                    {!isChatOpen && <Footer className="publicRoutesFooter" />}
                </div>
            </div>
        )
    }

    // Show loader while redirecting to login (prevents white screen)
    if (isPostAuthRoute && !shouldRedirectExternally && !isPublicRoute) {
        return (
            <LoaderOverlay
                className="loaderPublicRoutesRedirect"
                title=""
            >
                <div
                    className="redirectingToLogin"
                    style={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        fontSize: 20,
                        marginTop: 10,
                    }}
                >
                    Redirecting to login...
                </div>
            </LoaderOverlay>
        );
    }

    // Show routes for public paths
    return renderRoutes();
}

const sty: Record<string, CSSProperties> = {
    wrapper: {
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
    },
    publicRoutesComponent: {
        display: 'flex',
        width: '100%',
        flex: 1,
    },
    contentArea: {
        flex: 1,
        width: '100%',
    }
}

export default PublicRoutes;